import type { BootstrapPasswordStoreRequest, PasswordKeyringDTO } from "../../common/api/types.js";

const DERIVATION_ITERATIONS = 600_000;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

export interface EncryptedValue {
  ciphertext: string;
  iv: string;
}

export interface PasswordSecret {
  title: string;
  username: string;
  password: string;
  url: string;
  notes: string;
}

export async function createPasswordStore(passphrase: string): Promise<{ request: BootstrapPasswordStoreRequest; privateKey: CryptoKey; workspaceKey: CryptoKey }> {
  const pair = await crypto.subtle.generateKey({ name: "RSA-OAEP", modulusLength: 3072, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256" }, true, ["encrypt", "decrypt"]);
  const publicKey = await crypto.subtle.exportKey("spki", pair.publicKey);
  const privateKey = await crypto.subtle.exportKey("pkcs8", pair.privateKey);
  const derivationSalt = crypto.getRandomValues(new Uint8Array(16));
  const privateKeyIv = crypto.getRandomValues(new Uint8Array(12));
  const wrappingKey = await deriveWrappingKey(passphrase, derivationSalt, DERIVATION_ITERATIONS);
  const encryptedPrivateKey = await crypto.subtle.encrypt({ name: "AES-GCM", iv: privateKeyIv }, wrappingKey, privateKey);
  const workspaceKey = await generateWorkspaceKey();
  const wrappedWorkspaceKey = await wrapWorkspaceKey(workspaceKey, pair.publicKey);
  return {
    request: {
      publicKey: toBase64(publicKey),
      encryptedPrivateKey: toBase64(encryptedPrivateKey),
      privateKeyIv: toBase64(privateKeyIv),
      derivationSalt: toBase64(derivationSalt),
      derivationIterations: DERIVATION_ITERATIONS,
      wrappedWorkspaceKey,
    },
    privateKey: pair.privateKey,
    workspaceKey,
  };
}

export async function unlockPrivateKey(keyring: PasswordKeyringDTO, passphrase: string): Promise<CryptoKey> {
  try {
    const wrappingKey = await deriveWrappingKey(passphrase, fromBase64(keyring.derivationSalt), keyring.derivationIterations);
    const privateKey = await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromBase64(keyring.privateKeyIv) }, wrappingKey, fromBase64(keyring.encryptedPrivateKey));
    return await crypto.subtle.importKey("pkcs8", privateKey, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["decrypt"]);
  } catch {
    throw new Error("Das Vault-Passwort ist nicht korrekt.");
  }
}

export async function importPublicKey(value: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("spki", fromBase64(value), { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]);
}

export async function generateWorkspaceKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
}

export async function wrapWorkspaceKey(workspaceKey: CryptoKey, publicKey: CryptoKey): Promise<string> {
  const raw = await crypto.subtle.exportKey("raw", workspaceKey);
  return toBase64(await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, raw));
}

export async function unwrapWorkspaceKey(wrappedKey: string, privateKey: CryptoKey): Promise<CryptoKey> {
  const raw = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, privateKey, fromBase64(wrappedKey));
  return crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, true, ["encrypt", "decrypt"]);
}

export async function encryptSecret(secret: PasswordSecret, workspaceKey: CryptoKey): Promise<EncryptedValue> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, workspaceKey, encoder.encode(JSON.stringify(secret)));
  return { ciphertext: toBase64(ciphertext), iv: toBase64(iv) };
}

export async function decryptSecret(value: EncryptedValue, workspaceKey: CryptoKey): Promise<PasswordSecret> {
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: fromBase64(value.iv) }, workspaceKey, fromBase64(value.ciphertext));
  const parsed = JSON.parse(decoder.decode(plaintext)) as Partial<PasswordSecret>;
  return { title: parsed.title ?? "", username: parsed.username ?? "", password: parsed.password ?? "", url: parsed.url ?? "", notes: parsed.notes ?? "" };
}

export async function createPublicSecret(secret: PasswordSecret): Promise<{ encrypted: EncryptedValue; fragmentKey: string }> {
  const key = await generateWorkspaceKey();
  const encrypted = await encryptSecret(secret, key);
  const raw = await crypto.subtle.exportKey("raw", key);
  return { encrypted, fragmentKey: toBase64Url(raw) };
}

export async function decryptPublicSecret(value: EncryptedValue, fragmentKey: string): Promise<PasswordSecret> {
  const key = await crypto.subtle.importKey("raw", fromBase64Url(fragmentKey), { name: "AES-GCM" }, false, ["decrypt"]);
  return decryptSecret(value, key);
}

export function createPublicToken(): string {
  return toBase64Url(crypto.getRandomValues(new Uint8Array(32)));
}

async function deriveWrappingKey(passphrase: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey("raw", encoder.encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", hash: "SHA-256", salt, iterations }, material, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

function toBase64(value: ArrayBuffer | Uint8Array): string {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function toBase64Url(value: ArrayBuffer | Uint8Array): string {
  return toBase64(value).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  return fromBase64(base64);
}
